import { useContext, useRef } from 'react';

import {
  HoverCard,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
} from '@repo/videa-components/src/ui/hover-card';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';

import { ExternalPatientContext } from '@/app/external/constants';
import type { Appointment } from '@/react-query/useAppointments';

import { usePreferences } from '../../../../react-query/usePreferences';
import type { TVisitSummary } from '../../../../react-query/useVisitSummaries';
import { ToothIcon } from '../../../../svgs/toothIcon';
import { useUniversalToFDI } from '../../../../utils/internationalization/useUniversalToFDI';
import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';

import { getHexFromProcedureStatus } from './constants';
import { useIsBasicOdontogram } from './hooks';
import { HoverInfo } from './hoverInfo';
import type { ToothStatus } from './Tooth';
import { TreatmentStatusBadge } from './treatmentStatusBadge';
import { getRecommendedProcedures } from './utils';

// const cariesDepthToText: Record<TCariesDepth, string> = {
//   into_crown_and_root_dentin: 'into crown and root dentin',
//   into_crown_dentin: 'into crown dentin',
//   into_enamel: 'into enamel',
//   into_furcation_and_root_dentin: 'into furcation and root dentin',
//   into_root_dentin: 'into root dentin',
//   into_furcation_and_root_dentin_and_pulp:
//     'into furcation and root dentin and pulp',
//   into_pulp: 'into pulp',
//   into_pulp_and_crown_dentin: 'into pulp and crown dentin',
//   into_pulp_and_root_dentin: 'into pulp and root dentin',
//   into_pulp_and_root_dentin_and_crown_dentin:
//     'into pulp and root dentin and crown dentin',
//   unknown: '',
// };

export interface ToothHoverProps {
  activeImageId?: string;
  appointment?: Appointment;
  children: React.ReactNode;
  visitSummaryTooth?: TVisitSummary['teeth'][number];
  toothStatus: ToothStatus;
  visitSummary?: TVisitSummary;
  module?: 'image-viewer' | 'patient-list';
}

export const ToothHover = ({
  activeImageId,
  appointment,
  children,
  visitSummaryTooth,
  toothStatus,
  visitSummary,
  module,
}: ToothHoverProps) => {
  const {
    isIframe,
    externalPatientId,
    externalImageId,
    patientId,
    trialStatus,
    centralizedBilling: isCentralizedBilling,
  } = useContext(ExternalPatientContext);
  const { id: selectedPatientId } = useParams<{ id: string }>();
  const { displayToothNumber } = useUniversalToFDI();
  const { data: preferences } = usePreferences();
  const { trackEvent } = useTrackEvent();
  const hoverTimeoutRef = useRef<NodeJS.Timeout>();
  const isBasicOdontogram = useIsBasicOdontogram();
  const recommendedProcedures = getRecommendedProcedures(
    visitSummaryTooth,
    preferences,
    isBasicOdontogram,
    visitSummary?.isPerioPatient,
  );

  const aiFindings = visitSummaryTooth?.aiFindings ?? [];
  const procedures = visitSummaryTooth?.procedures ?? [];
  const treatments = visitSummaryTooth?.treatments ?? [];

  const scheduledTodayTreatments = treatments.filter(
    ({ status }) => status === 'scheduled_today',
  );
  const completedTreatments = treatments.filter(
    ({ status }) => status === 'completed',
  );
  const scheduledFutureTreatments = treatments.filter(
    ({ status }) => status === 'scheduled_future',
  );
  const unscheduledTreatments = treatments.filter(
    ({ status }) => status === 'unscheduled',
  );

  const scheduledTreatments = [
    ...scheduledTodayTreatments,
    ...scheduledFutureTreatments,
  ];

  const showHoverInfo =
    !!recommendedProcedures.length ||
    !!aiFindings.length ||
    !!scheduledTreatments.length ||
    !!unscheduledTreatments.length ||
    !!completedTreatments.length;

  return (
    <HoverCard
      openDelay={100}
      closeDelay={100}
      onOpenChange={(open) => {
        if (open) {
          // Set a timeout to track the event after 500ms of hovering
          hoverTimeoutRef.current = setTimeout(() => {
            trackEvent(mixpanelEvents.odontogramHoverMouthPart, {
              mouthPart: `tooth-${toothStatus.toothId}`,
              estimatedVisitId: visitSummary?.estimatedVisitId,
              unifiedAppointmentId: visitSummary?.unifiedAppointmentId,
              toothColor: toothStatus.status
                ? getHexFromProcedureStatus(
                    toothStatus.status,
                    module === 'image-viewer',
                  )
                : '',
              type: toothStatus.type,
              status: toothStatus.status,
              pmsPatientId: appointment?.patient.pmsPatientId,
              pmsAppointmentId: appointment?.appointment.pmsAppointmentId,
              imagingPatientId: appointment?.patient.imagingPatientId,
              isIframe: !!isIframe,
              externalPatientId,
              externalImageId,
              patientId: selectedPatientId ?? patientId,
              imageId: activeImageId,
              trialStatus,
              centralizedBilling: isCentralizedBilling,
            });
          }, 500); // Adjust this value as needed
        } else if (hoverTimeoutRef.current) {
          // Clear the timeout if the hover ends before the threshold
          clearTimeout(hoverTimeoutRef.current);
        }
      }}
    >
      <HoverCardTrigger>{children}</HoverCardTrigger>
      {/* The HoverCardPortal ensures that the HoverCardContent is rendered at the root level of the DOM to avoid z-index issues. */}
      <HoverCardPortal>
        <HoverCardContent
          data-testid="tooth-hover-card"
          className={classNames('vai-w-max vai-font-sans vai-z-[200]', {
            'vai-bg-white vai-border-slate-200 vai-text-slate-950':
              module !== 'image-viewer',
            'vai-bg-zinc-950 vai-border-zinc-800 vai-text-slate-100':
              module === 'image-viewer',
            '!vai-p-3': isIframe,
          })}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={classNames('vai-flex vai-flex-col', {
              'vai-gap-3': !isIframe,
              'vai-gap-2': isIframe,
            })}
          >
            <div className="vai-flex vai-flex-row vai-gap-4">
              <span className="vai-ml-1 vai-flex vai-items-center vai-gap-2 0 vai-text-base vai-font-bold">
                <ToothIcon
                  fill={module === 'image-viewer' ? '#FAFAFA' : '#3F3F46'}
                />
                {displayToothNumber(toothStatus.toothId)}
              </span>
              <TreatmentStatusBadge
                isDarkMode={module === 'image-viewer'}
                procedures={procedures}
                treatments={treatments}
                hasAnalysis={!!visitSummaryTooth?.hasAnalysis}
                isIndividualTooth
                isPerioPatient={visitSummary?.isPerioPatient}
              />
            </div>
            {showHoverInfo && (
              <HoverInfo
                module={module}
                aiFindingsList={aiFindings}
                recommendedProcedures={recommendedProcedures}
                completedTreatments={completedTreatments}
                scheduledTreatments={scheduledTreatments}
                notScheduledTreatments={unscheduledTreatments}
              />
            )}
          </div>
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
};
